import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface HeaderProps {
  overlay?: boolean;
}

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category/music' },
  { label: '搜索', to: '/search' },
  { label: '关于我们', to: '/about' },
];

export default function Header({ overlay = false }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const solid = !overlay || scrolled;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200/70' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-4">
        <Link
          to="/"
          className="flex items-center gap-2 shrink-0 cursor-pointer"
          onClick={() => setMenuOpen(false)}
        >
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className={`ri-music-2-fill text-xl ${solid ? 'text-background-50' : 'text-background-50'}`}></i>
          </span>
          <span className={`text-xl font-black tracking-tight ${solid ? 'text-foreground-950' : 'text-background-50'}`}>
            抖音
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((item) =>
            item.label === '分类' ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setCatOpen(true)}
                onMouseLeave={() => setCatOpen(false)}
              >
                <button
                  type="button"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${
                    solid
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  分类
                  <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {catOpen && (
                  <div className="absolute top-full left-0 pt-2 w-[420px]">
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-3 grid grid-cols-2 gap-1">
                      {categories.map((c) => (
                        <Link
                          key={c.key}
                          to={`/category/${c.key}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                        >
                          <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                            <i className={`${c.icon} text-base`}></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-800">{c.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    solid
                      ? isActive
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex-1" />

        {/* Desktop search */}
        <form onSubmit={submitSearch} className="hidden md:block relative w-56 lg:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索你感兴趣的视频"
            className="w-full h-9 pl-9 pr-3 rounded-full text-sm bg-secondary-100 text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 transition-all"
          />
        </form>

        <Link
          to="/about"
          className="hidden md:flex items-center gap-1 h-9 px-4 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-upload-2-line"></i>
          发布视频
        </Link>

        {/* Mobile menu button */}
        <button
          type="button"
          aria-label="菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer"
        >
          <i
            className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
              solid ? 'text-foreground-900' : 'text-background-50'
            }`}
          ></i>
        </button>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4 space-y-4">
            <form onSubmit={submitSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你感兴趣的视频"
                className="w-full h-10 pl-9 pr-3 rounded-full text-sm bg-secondary-100 text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300"
              />
            </form>
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2.5 rounded-md bg-background-100 text-sm font-medium text-foreground-800 cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-arrow-right-s-line text-primary-500"></i>
                  {item.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="text-xs font-label text-foreground-500 mb-2">热门分类</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    onClick={() => setMenuOpen(false)}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer flex items-center gap-1"
                  >
                    <i className={`${c.icon} text-sm`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}